export const formatDateToIso = (value) => {
  if (!value) {
    return "";
  }

  const parsed = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const day = String(parsed.getDate()).padStart(2, "0");
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const year = parsed.getFullYear();

  return `${year}-${month}-${day}`;
};

export const formatIsoDateDisplay = (isoDate) => {
  if (!isoDate) {
    return "";
  }

  const parts = isoDate.split("-");

  if (parts.length !== 3) {
    return isoDate;
  }

  const [year, month, day] = parts;

  return `${month}/${day}/${year}`;
};

export const formatTimeAgo = (isoDate) => {
  if (!isoDate) {
    return "";
  }

  const created = new Date(isoDate).getTime();
  const now = Date.now();
  const diffMs = Math.max(0, now - created);
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffMinutes < 60) {
    return `${Math.max(diffMinutes, 1)} minutes ago`;
  }

  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;
  }

  if (diffDays < 7) {
    return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
  }

  if (diffWeeks < 5) {
    return `${diffWeeks} week${diffWeeks === 1 ? "" : "s"} ago`;
  }

  return new Date(isoDate).toLocaleDateString("en-GB");
};

export const formatLeaveBalanceValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "00";
  }

  const numeric = Number(value);

  if (Number.isNaN(numeric)) {
    return "00";
  }

  if (Number.isInteger(numeric)) {
    return String(numeric).padStart(2, "0");
  }

  return String(numeric);
};

export const mapLeaveBalanceToCards = (balance) => {
  if (!balance) {
    return [];
  }

  const format = formatLeaveBalanceValue;

  return [
    { leaveNos: format(balance.appliedLeave), leaveType: "Applied leave" },
    { leaveNos: format(balance.opening), leaveType: "Opening" },
    { leaveNos: format(balance.generated), leaveType: "Generated" },
    { leaveNos: format(balance.utilized), leaveType: "Utilized" },
    { leaveNos: format(balance.closingBalance), leaveType: "Closing balance" },
    { leaveNos: format(balance.leaveWithoutPay), leaveType: "Leave without pay" },
    { leaveNos: format(balance.earnedLeave), leaveType: "Earned leave" },
    { leaveNos: format(balance.casualSickLeave), leaveType: "Casual/Sick leave" },
  ];
};

export const hasLeaveBalanceDetails = (balance) => {
  if (!balance) {
    return false;
  }

  const keys = [
    "appliedLeave",
    "opening",
    "generated",
    "utilized",
    "closingBalance",
    "leaveWithoutPay",
    "earnedLeave",
    "casualSickLeave",
  ];

  return keys.some((key) => Number(balance[key]) > 0);
};

export const eachDayInIsoRange = (startIso, endIso) => {
  if (!startIso || !endIso) {
    return [];
  }

  const days = [];
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return [];
  }

  const current = new Date(start);

  while (current <= end) {
    days.push(formatDateToIso(current));
    current.setDate(current.getDate() + 1);
  }

  return days;
};

export const parseHolidayDisplayDate = (dateStr) => {
  if (!dateStr) {
    return null;
  }

  const parts = dateStr.split("-");

  if (parts.length !== 3) {
    return null;
  }

  const [day, month, year] = parts.map(Number);

  if (!day || !month || !year) {
    return null;
  }

  return new Date(year, month - 1, day);
};

export const buildCalendarDayTooltipMap = ({ leaveHistory = [] } = {}) => {
  const map = {};
  const statusLabels = {
    PENDING: "Leave requested",
    APPROVED: "Leave approved",
    REJECTED: "Leave rejected",
  };

  leaveHistory.forEach((leave) => {
    const statusLabel = statusLabels[leave.status] || "Leave";
    const detail = `${statusLabel}: ${leave.leaveType || "Leave"}${
      leave.reason ? ` — ${leave.reason}` : ""
    } (${formatIsoDateDisplay(leave.startDate)} - ${formatIsoDateDisplay(leave.endDate)})`;

    eachDayInIsoRange(leave.startDate, leave.endDate).forEach((day) => {
      map[day] = map[day] ? `${map[day]}\n${detail}` : detail;
    });
  });

  return map;
};

export const buildCalendarDayStatusMap = ({
  leaveHistory = [],
  punches = [],
  todayIso = formatDateToIso(new Date()),
} = {}) => {
  const map = new Map();

  leaveHistory.forEach((leave) => {
    let status = null;

    if (leave.status === "APPROVED") {
      status = "leave-approved";
    } else if (leave.status === "PENDING") {
      status = "leave-pending";
    }

    if (!status) {
      return;
    }

    eachDayInIsoRange(leave.startDate, leave.endDate).forEach((day) => {
      const existing = map.get(day);

      if (status === "leave-approved" || existing !== "leave-approved") {
        map.set(day, status);
      }
    });
  });

  punches.forEach((punch) => {
    const day = punch.date;

    if (!day) {
      return;
    }

    // Present color applies only after the day completes (past dates).
    if (day >= todayIso) {
      return;
    }

    const leaveStatus = map.get(day);

    if (leaveStatus === "leave-approved" || leaveStatus === "leave-pending") {
      return;
    }

    const isPresent =
      punch.status === "PRESENT" ||
      (punch.checkIn && punch.checkIn !== "--:--" && punch.checkIn !== "--");

    if (isPresent) {
      map.set(day, "present");
    }
  });

  return map;
};

export const mapArRequestTypeLabel = (requestType) => {
  const labels = {
    FORGOT_IN: "Forgot In",
    FORGOT_OUT: "Forgot Out",
    BOTH: "Both",
  };

  return labels[requestType] || requestType;
};

export const mapAttendanceStatusLabel = (status) => {
  const labels = {
    PRESENT: "Present",
    ABSENT: "Absent",
    HALF_DAY: "Half day absent",
    HOLIDAY: "Holiday",
    LEAVE: "Leave",
  };

  return labels[status] || status;
};

export const mapCelebrationTypeLabel = (type, years) => {
  if (type === "WORK_ANNIVERSARY") {
    return `Work Anniversary${years ? ` (${years} years)` : ""}`;
  }

  return "Birthday";
};

export const mapLeaveTypeLabel = (leaveType) => {
  const labels = {
    PAID: "Paid",
    MEDICAL: "Medical",
    CASUAL: "Casual",
    UNPAID: "Leave without pay",
    PL: "PL",
    CL: "CL",
    SL: "SL",
    UL: "UL",
  };

  return labels[leaveType] || leaveType;
};

export const mapFeedPostForDisplay = (post) => ({
  id: post.postId,
  type: (post.type || "TEXT").toLowerCase(),
  author: post.author,
  authorInitials: post.authorInitials,
  timeAgo: formatTimeAgo(post.createdAt),
  content: post.content,
  image: post.imageUrl || null,
  imageAlt: post.imageAlt,
  imageCaption: post.imageCaption,
  imagePlaceholder: post.imagePlaceholder,
  copyPost: post.copyPost,
});

export const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
